/**
 * buildStripeAppearance
 *
 * Builds a Stripe Elements `appearance` object from the app's LIVE theme so the
 * Payment Element matches the rest of the UI. Exactly ONE value is read at
 * runtime: `--theme-600`, the brand primary.
 *
 * How much that buys you is per-app. `--theme-600` is only overridden where
 * `applyTheme` runs (`styles/themeUtils.ts`), and today that is **mobile-app
 * only** (`ThemeProvider`, `StartPage`, `FindClubPage`) — so mobile really
 * does re-brand per tenant club, while admin and web2 always read the `tokens.css`
 * default `#7F56D9`. That is the correct brand for both (it matches
 * `butter-brand-600`), and it fixed web2's previously hardcoded off-brand blue —
 * but they get a constant, not tenant theming. If admin or web2 ever adopt
 * `applyTheme`, they inherit the per-tenant behaviour here for free.
 *
 * Everything else — text / danger / background / font / radius — is a literal
 * token from the Tailwind preset (`tailwind.preset.ts` / `tokens.css`) mirrored
 * here as a constant, and is therefore light-mode only today. When the theme var
 * is absent (SSR, tests, or before `applyTheme` runs) the one read falls back to
 * the default purple brand, so the returned object is always valid.
 *
 * Pass the result to `StripePaymentElementForm` / `useStripePaymentElement`'s
 * `appearance` prop. It's read once at mount (the hook holds it in a ref), so
 * memoize at the call site if you want to avoid rebuilding on every render.
 *
 * @module domains/billing/stripeAppearance
 */

/** Default `--theme-600` (purple brand) — mirrors `tokens.css` for the fallback path. */
const DEFAULT_THEME_PRIMARY = '#7F56D9'

/** Read a CSS custom property off `:root`, falling back when unavailable. */
function readThemeVar(name: string, fallback: string): string {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') {
    return fallback
  }
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

/** A Stripe Elements `appearance` object (structural — we don't depend on stripe-js types here). */
export interface StripeAppearance {
  theme: 'stripe'
  variables: Record<string, string>
}

/**
 * Build a theme-derived Stripe `appearance`. `colorPrimary` follows the live
 * per-tenant theme (`--theme-600`); the remaining variables mirror the Tailwind
 * preset's literal tokens so the element matches the rest of the app.
 */
export function buildStripeAppearance(): StripeAppearance {
  return {
    theme: 'stripe',
    variables: {
      colorPrimary: readThemeVar('--theme-600', DEFAULT_THEME_PRIMARY),
      colorBackground: '#FFFFFF',
      colorText: '#0A0D12', // preset `foreground`
      colorDanger: '#FF383C', // preset `butter.error.500` / `destructive`
      fontFamily: 'Urbanist, system-ui, -apple-system, sans-serif',
      borderRadius: '0.5rem',
    },
  }
}
